'use strict';

import moment from 'moment-timezone';
import actions from './actions';
import getters from './getters';
import mutations from './mutations';

const defaultInteractionHistoryPeriods = [
  {
    i18nKey: 'preferencesSettings.periodDaily',
    value: 24
  },
  {
    i18nKey: 'preferencesSettings.periodWeekly',
    value: 168
  },
  {
    i18nKey: 'preferencesSettings.periodMonthly',
    value: 720
  }
];

const defaultIdleTimesToAgentIntelligenceCenter = [
  {
    i18nKey: 'preferencesSettings.idleTimesToAgentIntelligenceCenter.none',
    value: -1
  },
  {
    i18nKey: 'preferencesSettings.idleTimesToAgentIntelligenceCenter.10sec',
    value: 10
  },
  {
    i18nKey: 'preferencesSettings.idleTimesToAgentIntelligenceCenter.30sec',
    value: 30
  },
  {
    i18nKey: 'preferencesSettings.idleTimesToAgentIntelligenceCenter.1min',
    value: 60
  },
  {
    i18nKey: 'preferencesSettings.idleTimesToAgentIntelligenceCenter.5min',
    value: 300
  }
];

const defaultAlertsEndOfBreak = [
  {
    i18nKey: 'preferencesSettings.alertEndOfBreak.none',
    value: 0
  },
  {
    i18nKey: 'preferencesSettings.alertEndOfBreak.10sec',
    value: 10
  },
  {
    i18nKey: 'preferencesSettings.alertEndOfBreak.30sec',
    value: 30
  },
  {
    i18nKey: 'preferencesSettings.alertEndOfBreak.1min',
    value: 60
  },
  {
    i18nKey: 'preferencesSettings.alertEndOfBreak.5min',
    value: 300
  }
];

const defaultLanguages = [
  {
    i18nKey: 'applicationSettings.languageOptions.english',
    value: 'en'
  },
  {
    i18nKey: 'applicationSettings.languageOptions.spanish',
    value: 'es'
  },
  {
    i18nKey: 'applicationSettings.languageOptions.french',
    value: 'fr'
  },
  {
    i18nKey: 'applicationSettings.languageOptions.german',
    value: 'de'
  },
  {
    i18nKey: 'applicationSettings.languageOptions.dutch',
    value: 'nl'
  }
];

const defaultTimeFormats = [
  {
    i18nKey: 'applicationSettings.timeFormatOptions.twelve',
    value: 'hh:mm A'
  },
  {
    i18nKey: 'applicationSettings.timeFormatOptions.twenty-four',
    value: 'HH:mm'
  }
];

const defaultIncomingInteractionDisplayModes = [
  {
    i18nKey: 'preferencesSettings.banner',
    value: 'banner'
  },
  {
    i18nKey: 'preferencesSettings.panel',
    value: 'panel'
  },
  {
    i18nKey: 'preferencesSettings.bannerAndPannel',
    value: 'bannerAndPanel'
  }
];

const getDefaultApplicationTimeZones = () => {
  const timeZones = [];

  const recommendedTimeZone = moment.tz.guess();
  const americanZones = moment.tz.zonesForCountry('US');
  const canadianZones = moment.tz.zonesForCountry('CA');

  const convertToListObject = (timezone, country) => {
    let prefix = '';
    if (!country) {
      if (americanZones.includes(timezone)) {
        prefix = '[US]';
      } else if (canadianZones.includes(timezone)) {
        prefix = '[CAN]';
      }
    } else if (country === 'US') {
      prefix = '[US]';
    } else if (country === 'CA') {
      prefix = '[CAN]';
    }
    const name = timezone
      .replace('America/', '')
      .replace('Pacific/', '')
      .replace(/_/g, ' ');
    return {
      text: `${prefix} ${name} (${moment.tz(timezone).format('z Z')})`,
      value: timezone
    };
  };

  timeZones.push(convertToListObject(recommendedTimeZone));
  timeZones.push({ divider: true });
  timeZones.push(...americanZones.map(z => convertToListObject(z, 'US')));
  timeZones.push(...canadianZones.map(z => convertToListObject(z, 'CA')));

  return timeZones;
};

const state = {
  availableBusinessProcesses: [],
  availableTimeZones: [],
  defaultTimeZone: 'Eastern Standard Time',
  availableApplicationTimeZones: getDefaultApplicationTimeZones(),
  availableAgentStates: [],
  availableCustomBreakStates: [],
  availableInteractionHistoryPeriods: defaultInteractionHistoryPeriods,
  availableIdleTimesToAgentIntelligenceCenter: defaultIdleTimesToAgentIntelligenceCenter,
  availableAlertsEndOfBreak: defaultAlertsEndOfBreak,
  availableLanguages: defaultLanguages,
  availableTimeFormats: defaultTimeFormats,
  availableIncomingInteractionDisplayModes: defaultIncomingInteractionDisplayModes,
  availableIncomingInteractionRingtones: [],
  smtpDetails: {
    businessProcesses: [],
    defaultBccEmailAddress: '',
    defaultFromEmailAddress: '',
    outgoingEmailMode: 0
  },
  smsDetails: [],
  valueSets: [],
  supervisorKPIsDescription: []
};

export default {
  namespaced: true,

  /**
   * As a best practice: Actions call mutations which update our current state
   * Actions could execute sync or async operations.
   */
  actions,

  /**
   * Access to state values
   */
  getters,

  /**
   * Used to commit and track state changes
   * Mutations should always execute sync operations.
   */
  mutations,

  /**
   * Single source of truth
   */
  state
};
